import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import api, { formatApiError } from "../lib/api";
import { openExternal } from "../lib/openExternal";
import { Capacitor } from "@capacitor/core";
import { Star, Lock, ArrowRight, CheckCircle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * /upgrade — single hub for the "I want Pro" flow.
 *
 * Adapts to runtime state via GET /api/billing/upgrade-status:
 *   • is_pro=true            → "You're on Pro" + manage panel.
 *   • stripe_enabled=true    → plan picker → POST /api/billing/checkout-session.
 *   • stripe_enabled=false   → "Request Pro access" form → POST
 *                              /api/billing/request-pro (admin gets email,
 *                              user sees pending state on refresh).
 *
 * Anonymous users are bounced to /login with `?next=/upgrade` so they
 * never see a logged-out empty state.
 *
 * Designed so flipping STRIPE_ENABLED=true in prod transparently upgrades
 * everyone from the request flow to real checkout — no UI changes required.
 */
export default function Upgrade() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const claimedSlug = params.get("claim") || "";

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent("/upgrade" + (claimedSlug ? `?claim=${claimedSlug}` : ""))}`, { replace: true });
      return;
    }
    let mounted = true;
    api.get("/billing/upgrade-status")
      .then(({ data }) => mounted && setStatus(data))
      .catch(() => mounted && toast.error("Could not load upgrade status"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [user, authLoading, navigate, claimedSlug]);

  const handleCheckout = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout-session", {
        plan,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        // Native: MUST link out to the DEFAULT browser (Safari) — App
        // Review rejected in-app-sheet checkout under Guideline 3.1.1
        // (Aug 2026). Web: same-tab navigation so Stripe's redirect_url
        // completes the session_id round-trip back to /billing/success.
        if (Capacitor.isNativePlatform()) {
          toast.info("Continuing to secure checkout in your browser…");
          await openExternal(data.url, { mode: 'system' });
        } else {
          await openExternal(data.url, { mode: 'same-tab' });
        }
      } else {
        toast.error("Checkout unavailable — try again in a moment.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start checkout");
    } finally {
      setBusy(false);
    }
  };

  const handleRequestPro = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/billing/request-pro", { note: note.trim() || null });
      toast.success("Request sent. We'll email you once Pro is unlocked.");
      // Optimistically flip pending=true so the UI doesn't bounce back.
      setStatus((s) => ({ ...s, pending_request: true }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit request");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center" data-testid="upgrade-loading">
        <Spinner className="h-6 w-6 text-primary animate-spin" />
      </div>
    );
  }
  if (!user || !status) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14" data-testid="upgrade-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-primary/10 text-primary text-[11px] font-bold uppercase tracking-wider mb-3">
          <Star weight="fill" className="h-3.5 w-3.5" /> TruckTrackr Pro
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
          {status.is_pro ? "You're on Pro." : "Unlock the full profile."}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-2">
          {status.is_pro
            ? "Every Pro feature is active on your account."
            : "Add a cover photo, gallery, description, menu, hours, social links, and online ordering — turn map clicks into directions."}
        </p>
      </div>

      {status.is_pro ? (
        <ProActiveCard onDone={() => navigate("/dashboard")} />
      ) : status.stripe_enabled ? (
        <>
          <InAppBrowserWarning />
          <StripePlanPicker busy={busy} onPick={handleCheckout} />
        </>
      ) : (
        <RequestProForm
          busy={busy}
          pending={status.pending_request}
          note={note}
          setNote={setNote}
          onSubmit={handleRequestPro}
        />
      )}

      <FeatureGrid />

      {claimedSlug && (
        <div className="mt-6 text-center">
          <Link
            to={`/t/${claimedSlug}`}
            className="text-sm text-muted-foreground hover:text-foreground"
            data-testid="upgrade-back-to-profile"
          >
            ← Back to /t/{claimedSlug}
          </Link>
        </div>
      )}
    </div>
  );
}

function ProActiveCard({ onDone }) {
  return (
    <div
      className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 sm:p-8 text-center"
      data-testid="upgrade-pro-active"
    >
      <CheckCircle weight="fill" className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
      <h2 className="font-display font-bold text-xl mb-1">Pro is active</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Head to the dashboard to customize your profile, schedule events, and access Pro features.
      </p>
      <button
        type="button"
        onClick={onDone}
        className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors"
        data-testid="upgrade-go-dashboard"
      >
        Go to dashboard <ArrowRight weight="bold" className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * InAppBrowserWarning — Stripe checkout often breaks inside Facebook /
 * Instagram in-app browsers (they intercept redirects, mangle cookies,
 * and prevent payment-sheet autofill). Real bug report Feb 2026 from
 * a user who reached the upgrade page via a Facebook ad (fbclid in URL)
 * and watched the checkout crash mid-payment.
 *
 * Detection is intentionally conservative — only flag obvious in-app
 * UAs so real Safari/Chrome users never see this banner. If the user
 * is on Twitter/X, LinkedIn, or TikTok in-app, we surface the same
 * warning since they all share the same WebView limitations.
 */
function InAppBrowserWarning() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  // FBAN/FBAV = Facebook app, Instagram = IG app, Line/Twitter/etc.
  const isInApp = /(FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|LinkedInApp|TikTok)/i.test(ua);
  if (!isInApp) return null;
  const url = typeof window !== "undefined" ? window.location.href : "";
  return (
    <div
      className="mb-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-4 text-sm"
      data-testid="upgrade-in-app-browser-warning"
    >
      <p className="font-semibold text-amber-900 dark:text-amber-300 mb-1">
        Open in your browser for payment
      </p>
      <p className="text-amber-900/90 dark:text-amber-200/90 text-xs leading-relaxed">
        You&apos;re viewing this inside a social media app. Payment
        checkout can crash here — tap the <span className="font-semibold">⋯</span> menu and choose
        <span className="font-semibold"> &ldquo;Open in Safari/Chrome&rdquo;</span> before continuing.
      </p>
      {url && (
        <button
          type="button"
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-900 dark:text-amber-300 underline"
          onClick={() => {
            try {
              if (navigator.clipboard) {
                navigator.clipboard.writeText(url);
              }
            } catch { /* ignore */ }
          }}
          data-testid="upgrade-copy-url"
        >
          Copy this page URL
        </button>
      )}
    </div>
  );
}

function StripePlanPicker({ busy, onPick }) {
  return (
    <div className="grid sm:grid-cols-1 gap-3" data-testid="upgrade-stripe-plans">
      <PlanCard
        title="Pro Monthly"
        price="$15"
        cadence="/month"
        sub="14-day free trial · cancel anytime."
        accent
        ctaLabel="Start free trial"
        ctaTestId="upgrade-plan-monthly"
        busy={busy}
        onClick={() => onPick("monthly")}
      />
    </div>
  );
}

function PlanCard({ title, price, cadence, sub, accent, ctaLabel, ctaTestId, busy, onClick }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
      <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-display font-bold text-3xl">{price}</span>
        <span className="text-sm text-muted-foreground">{cadence}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>
      <button
        type="button"
        disabled={busy}
        onClick={onClick}
        data-testid={ctaTestId}
        className={
          "mt-4 w-full h-11 rounded-full font-semibold text-sm transition-colors disabled:opacity-50 " +
          (accent
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "border border-border hover:bg-muted")
        }
      >
        {busy ? "Loading…" : ctaLabel}
      </button>
    </div>
  );
}

function RequestProForm({ busy, pending, note, setNote, onSubmit }) {
  if (pending) {
    return (
      <div
        className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 sm:p-8 text-center"
        data-testid="upgrade-pending"
      >
        <Lock weight="duotone" className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h2 className="font-display font-bold text-xl mb-1">Request received</h2>
        <p className="text-sm text-muted-foreground">
          We'll email you once your Pro upgrade is approved — usually within one business day.
        </p>
      </div>
    );
  }
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-4"
      data-testid="upgrade-request-form"
    >
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-primary">Private beta</div>
        <h2 className="font-display font-bold text-xl mt-1">Request Pro access</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Pro is in private beta. Tell us about your truck and we'll unlock it on your account
          — usually within one business day.
        </p>
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground" htmlFor="upgrade-note">
          A line or two about your truck (optional)
        </label>
        <textarea
          id="upgrade-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={500}
          className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
          placeholder="E.g. Wood-fired pizza, Lancaster County, 3 events/week."
          data-testid="upgrade-note-input"
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="upgrade-request-submit"
        className="w-full h-11 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
      >
        {busy ? "Submitting…" : <>Request Pro access <ArrowRight weight="bold" className="h-4 w-4" /></>}
      </button>
    </form>
  );
}

function FeatureGrid() {
  const items = [
    "Cover photo & branded hero",
    "Photo gallery (lightbox)",
    "Description & known-for",
    "Menu (PDF/image + link)",
    "Social links (FB / IG / TikTok)",
    "Hours & contact info",
    "Online ordering button",
    "Insights & analytics tiles",
    "Google Calendar two-way sync",
    "Outlook Calendar two-way sync",
    "Apple Calendar subscription feed",
    "Photo Cards weekly schedule images",
    "Remove TruckTrackr branding from posts",
  ];
  return (
    <div className="mt-8" data-testid="upgrade-feature-grid">
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
        What you unlock
      </div>
      <ul className="grid sm:grid-cols-2 gap-2">
        {items.map((label) => (
          <li
            key={label}
            className="flex items-center gap-2 text-sm rounded-xl border border-border bg-card px-3 py-2"
          >
            <CheckCircle weight="fill" className="h-4 w-4 text-primary flex-shrink-0" />
            <span>{label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
